import type { DriverGroup } from "./usePickupAssignmentDriversColumn";

/** Duty values from `mapDutyStatusToUi` / `driverStatus` on `DriverGroup`. */
export type DriverDutyFacet = "AVAILABLE" | "ON_DUTY" | "OFF_DUTY";

/** Delivery or pickup workload bucket (OR within one dimension). */
export type ProgressFacet = "none" | "has_remaining" | "all_done";

export type DriverColumnFacets = {
  /** Empty = all duties. */
  duty: DriverDutyFacet[];
  /** Empty = all. */
  deliveries: ProgressFacet[];
  /** Empty = all. */
  pickups: ProgressFacet[];
};

export const defaultDriverColumnFacets = (): DriverColumnFacets => ({
  duty: [],
  deliveries: [],
  pickups: [],
});

/** Product default for Pickup Assignment V2 drivers column: on-duty only. */
export function defaultDriversToolbarFacets(): DriverColumnFacets {
  return { duty: ["ON_DUTY"], deliveries: [], pickups: [] };
}

const DUTY_SET: ReadonlySet<string> = new Set(["AVAILABLE", "ON_DUTY", "OFF_DUTY"]);
const PROGRESS_SET: ReadonlySet<string> = new Set([
  "none",
  "has_remaining",
  "all_done",
]);

/** Restore from localStorage with safe defaults. */
export function sanitizeDriverColumnFacets(raw: unknown): DriverColumnFacets {
  const d = defaultDriverColumnFacets();
  if (!raw || typeof raw !== "object") return d;
  const o = raw as Record<string, unknown>;
  const duty = (Array.isArray(o.duty) ? o.duty : []).filter(
    (x): x is DriverDutyFacet => typeof x === "string" && DUTY_SET.has(x)
  );
  const deliveries = (Array.isArray(o.deliveries) ? o.deliveries : []).filter(
    (x): x is ProgressFacet => typeof x === "string" && PROGRESS_SET.has(x)
  );
  const pickups = (Array.isArray(o.pickups) ? o.pickups : []).filter(
    (x): x is ProgressFacet => typeof x === "string" && PROGRESS_SET.has(x)
  );
  return { duty, deliveries, pickups };
}

export function normalizeDutyStatus(raw: string): string {
  return String(raw || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

export function driverMatchesDutyFacets(
  driver: DriverGroup,
  facets: DriverDutyFacet[]
): boolean {
  if (facets.length === 0) return true;
  const st = normalizeDutyStatus(driver.driverStatus);
  return facets.some((f) => st === f);
}

export function driverMatchesDeliveryFacets(
  driver: DriverGroup,
  facets: ProgressFacet[]
): boolean {
  if (facets.length === 0) return true;
  const n = driver.numberOfDeliveries;
  const c = driver.deliveriesCompleted;
  return facets.some((facet) => {
    if (facet === "none") return n === 0;
    if (facet === "has_remaining") return n > 0 && c < n;
    return n > 0 && c === n;
  });
}

export function driverMatchesPickupFacets(
  driver: DriverGroup,
  facets: ProgressFacet[]
): boolean {
  if (facets.length === 0) return true;
  const n = driver.numberOfPickups;
  const c = driver.pickupsCompleted;
  return facets.some((facet) => {
    if (facet === "none") return n === 0;
    if (facet === "has_remaining") return n > 0 && c < n;
    return n > 0 && c === n;
  });
}

/** AND across dimensions; OR within each dimension. */
export function filterDriverGroupsByFacets(
  drivers: DriverGroup[],
  facets: DriverColumnFacets
): DriverGroup[] {
  return drivers.filter(
    (d) =>
      driverMatchesDutyFacets(d, facets.duty) &&
      driverMatchesDeliveryFacets(d, facets.deliveries) &&
      driverMatchesPickupFacets(d, facets.pickups)
  );
}

export function facetsAreActive(facets: DriverColumnFacets): boolean {
  return (
    facets.duty.length > 0 ||
    facets.deliveries.length > 0 ||
    facets.pickups.length > 0
  );
}

export type DriverGroupSortKey =
  | "remaining_work"
  | "name"
  | "duty"
  | "delivery_ratio"
  | "pickup_ratio";

export const DRIVER_GROUP_SORT_KEYS: readonly DriverGroupSortKey[] = [
  "remaining_work",
  "name",
  "duty",
  "delivery_ratio",
  "pickup_ratio",
] as const;

export function isDriverGroupSortKey(v: unknown): v is DriverGroupSortKey {
  return typeof v === "string" && (DRIVER_GROUP_SORT_KEYS as readonly string[]).includes(v);
}

export type SortDirection = "asc" | "desc";

export const DEFAULT_DRIVER_SORT_KEY: DriverGroupSortKey = "name";

export const DEFAULT_DRIVER_SORT_DIRECTION: SortDirection = "asc";

function sortedDutyTuple(f: DriverDutyFacet[]): string {
  return [...f].sort().join(",");
}

function sortedProgressTuple(f: ProgressFacet[]): string {
  return [...f].sort().join(",");
}

export function driverColumnFacetsEqual(a: DriverColumnFacets, b: DriverColumnFacets): boolean {
  return (
    sortedDutyTuple(a.duty) === sortedDutyTuple(b.duty) &&
    sortedProgressTuple(a.deliveries) === sortedProgressTuple(b.deliveries) &&
    sortedProgressTuple(a.pickups) === sortedProgressTuple(b.pickups)
  );
}

export function driversToolbarMatchesDefaults(
  facets: DriverColumnFacets,
  sortKey: DriverGroupSortKey,
  sortDirection: SortDirection
): boolean {
  return (
    driverColumnFacetsEqual(facets, defaultDriversToolbarFacets()) &&
    sortKey === DEFAULT_DRIVER_SORT_KEY &&
    sortDirection === DEFAULT_DRIVER_SORT_DIRECTION
  );
}

export function remainingIncompleteWork(driver: DriverGroup): number {
  return (
    Math.max(0, driver.numberOfPickups - driver.pickupsCompleted) +
    Math.max(0, driver.numberOfDeliveries - driver.deliveriesCompleted)
  );
}

/** Lower = higher priority in dispatch lists (available first). */
export function dutyOrdinal(driver: DriverGroup): number {
  const st = normalizeDutyStatus(driver.driverStatus);
  if (st === "AVAILABLE") return 0;
  if (st === "ON_DUTY") return 1;
  if (st === "OFF_DUTY") return 2;
  return 3;
}

/**
 * Completion ratio in [0, 1], or `null` when there is no work in that category
 * (used to pin zero-delivery / zero-pickup rows to the end for ratio sorts).
 */
export function deliveryCompletionRatio(driver: DriverGroup): number | null {
  const n = driver.numberOfDeliveries;
  if (n === 0) return null;
  return driver.deliveriesCompleted / n;
}

export function pickupCompletionRatio(driver: DriverGroup): number | null {
  const n = driver.numberOfPickups;
  if (n === 0) return null;
  return driver.pickupsCompleted / n;
}

function compareName(a: DriverGroup, b: DriverGroup): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

/**
 * Stable sort: primary comparator from `sortKey` / `direction`, then name.
 */
export function sortDriverGroups(
  drivers: DriverGroup[],
  sortKey: DriverGroupSortKey,
  direction: SortDirection
): DriverGroup[] {
  const dir = direction === "asc" ? 1 : -1;
  const out = [...drivers];
  out.sort((a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case "remaining_work": {
        cmp = remainingIncompleteWork(a) - remainingIncompleteWork(b);
        cmp *= dir;
        break;
      }
      case "name": {
        cmp = compareName(a, b) * dir;
        break;
      }
      case "duty": {
        cmp = (dutyOrdinal(a) - dutyOrdinal(b)) * dir;
        break;
      }
      case "delivery_ratio": {
        const ra = deliveryCompletionRatio(a);
        const rb = deliveryCompletionRatio(b);
        if (ra == null && rb == null) cmp = 0;
        else if (ra == null) cmp = 1;
        else if (rb == null) cmp = -1;
        else cmp = (ra - rb) * dir;
        break;
      }
      case "pickup_ratio": {
        const ra = pickupCompletionRatio(a);
        const rb = pickupCompletionRatio(b);
        if (ra == null && rb == null) cmp = 0;
        else if (ra == null) cmp = 1;
        else if (rb == null) cmp = -1;
        else cmp = (ra - rb) * dir;
        break;
      }
      default:
        cmp = 0;
    }
    if (cmp !== 0) return cmp;
    return compareName(a, b);
  });
  return out;
}

const STORAGE_PREFIX = "pickupAssignmentV2:driversToolbar";

export type DriversToolbarPersisted = {
  sortKey: DriverGroupSortKey;
  sortDirection: SortDirection;
  facets: DriverColumnFacets;
};

export function loadDriversToolbarState(
  terminalId: number | null
): Partial<DriversToolbarPersisted> | null {
  if (terminalId == null || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}:${terminalId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DriversToolbarPersisted>;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function saveDriversToolbarState(
  terminalId: number | null,
  state: DriversToolbarPersisted
): void {
  if (terminalId == null || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}:${terminalId}`,
      JSON.stringify(state)
    );
  } catch {
    /* ignore quota / private mode */
  }
}
